import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import "@/App.css";
const Simulator = lazy(() => import("@/components/simulator/Simulator"));
const ShopPanel = lazy(() => import("@/components/shop/ShopPanel"));
import { Toaster, toast } from "sonner";
import { Loader2 } from "lucide-react";
import { WorkspaceProvider, useWorkspace } from "@/store/WorkspaceContext";
import { AuthProvider, useAuth } from "@/store/AuthContext";
import { Toolbar } from "@/components/workspace/Toolbar";
import { MenuBar } from "@/components/workspace/MenuBar";
import { CalibrationDialog } from "@/components/workspace/CalibrationDialog";
import { ProfileDialog } from "@/components/workspace/ProfileDialog";
import { Sidebar } from "@/components/workspace/Sidebar";
import { FilmCanvas } from "@/components/workspace/FilmCanvas";
import { DecalPreview } from "@/components/workspace/DecalPreview";
import { StickerStudio } from "@/components/stickers/StickerStudio";
import { CutBoardBar } from "@/components/workspace/CutBoardBar";
import { CutDialog } from "@/components/workspace/CutDialog";
import { NoPlotterDialog } from "@/components/workspace/NoPlotterDialog";
import { RollErrorDialog } from "@/components/workspace/RollErrorDialog";
import { TrialAlertDialog } from "@/components/workspace/TrialAlertDialog";
import { PasswordPromptDialog } from "@/components/workspace/PasswordPromptDialog";
import { HonorDialog } from "@/components/workspace/HonorDialog";
import { SettingsDialog } from "@/components/workspace/SettingsDialog";
import { StripFitDialog } from "@/components/workspace/StripFitDialog";
import { VersionHistoryDialog } from "@/components/workspace/VersionHistoryDialog";
import { JobHistoryDialog } from "@/components/workspace/JobHistoryDialog";
import { SupportedPlottersDialog } from "@/components/workspace/SupportedPlottersDialog";
import { VevorGuideDialog } from "@/components/workspace/VevorGuideDialog";
import { WatchVideoDialog } from "@/components/workspace/WatchVideoDialog";
import { ImportDialog } from "@/components/workspace/ImportDialog";
import { SubscribeDialog } from "@/components/workspace/SubscribeDialog";
import { TrialBanner } from "@/components/workspace/AccountMenu";
import { PreviewBar } from "@/components/workspace/PreviewBar";
import AuthPage from "@/pages/AuthPage";
import PaymentGate from "@/pages/PaymentGate";
import RenewalGate from "@/pages/RenewalGate";
import { WelcomeDialog, useWelcomePending } from "@/components/onboarding/WelcomeDialog";
import { TermsGate, needsTerms } from "@/components/onboarding/TermsGate";
import { GuidedTour, startTour } from "@/components/onboarding/GuidedTour";
import { WelcomeIntro } from "@/components/workspace/WelcomeIntro";

// Owner dashboard → Opening scene → "Preview": replays the greeting once with that scene (window event from ServiceLaunchControl)
const IntroPreview = ({ user }) => {
  const [scene, setScene] = useState(null);
  useEffect(() => { const h = (e) => setScene(e.detail?.scene || "auto"); window.addEventListener("xpattern:intro-preview", h); return () => window.removeEventListener("xpattern:intro-preview", h); }, []);
  if (!scene || !user) return null;
  return <WelcomeIntro key={scene + Date.now()} user={user} preview={scene} onDone={() => setScene(null)} />;
};
import { DemoLanding, demoCodeFromUrl } from "@/pages/DemoLanding";
import Site from "@/pages/Site";
import { isStoreBuild } from "@/lib/store";
import PaymentResultPage from "@/pages/PaymentResultPage";
import PayDone from "@/pages/PayDone";
import EstimatePage from "@/pages/EstimatePage";
import InvoicePage from "@/pages/InvoicePage";
import BookPage from "@/pages/BookPage";
import OfferLanding from "@/pages/OfferLanding";
import BuyerLanding from "@/pages/BuyerLanding";
import { AdminDialog } from "@/components/workspace/AdminDialog";
import { RequestVehicleDialog } from "@/components/workspace/RequestVehicleDialog";
import { api, apiError } from "@/lib/api";
import { loadConnection } from "@/lib/transport";
import { useBridgeAutodetect } from "@/hooks/useBridgeAutodetect";
import { useCutWarmup } from "@/hooks/useCutWarmup";
import { useCutSettings } from "@/hooks/useCutSettings";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { OfflineBanner } from "@/components/OfflineBanner";
import { ViewingAsBanner } from "@/components/workspace/ViewingAsBanner";
import { OwnerNotice } from "@/components/workspace/OwnerNotice";
import { OfflineModeBanner } from "@/components/workspace/OfflineBanner";
import { useOfflineSync } from "@/hooks/useOfflineSync";
import { useCopyShield } from "@/hooks/useCopyShield";
import { setMonitorUser } from "@/lib/monitor";

const Workspace = ({ locked = false }) => {
  const { state, dispatch } = useWorkspace();
  const { user, refresh } = useAuth();
  // gates (plan / terms / welcome) hold the service hub back
  useEffect(() => { dispatch({ type: "SET", patch: { locked } }); }, [locked]); // eslint-disable-line react-hooks/exhaustive-deps
  useBridgeAutodetect();
  useCutWarmup();
  useCutSettings(user?.id);
  useOfflineSync(user);
  useCopyShield(user);
  useEffect(() => { setMonitorUser(user?.id); }, [user?.id]);
  useEffect(() => { if (user?.id && !locked && new URLSearchParams(window.location.search).get("shop")) dispatch({ type: "SET", patch: { shop: true } }); }, [user?.id, locked]); // back from Stripe onboarding (/?shop=payments)
  useEffect(() => {
    if (user?.id && (user.shop_code || "") !== state.connection?.shopCode) dispatch({ type: "SET", patch: { connection: loadConnection(user.shop_code, user.id) } });
  }, [user?.shop_code]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user) return;
    // win-back link opened while signed out: the subscriber signed in, now accept it and show the result in My Dashboard
    const wb = localStorage.getItem("xpattern.winback");
    if (wb) {
      localStorage.removeItem("xpattern.winback");
      api.acceptOffer(wb).then(async (r) => { await refresh(); dispatch({ type: "SET", patch: { dialog: "profile" } }); toast.success(`You're staying — ${r.offer?.label || "your offer"} is on your subscription.`, { duration: 9000 }); }).catch((e) => toast.error(apiError(e, "Could not apply this offer")));
    }
    const code = new URLSearchParams(window.location.search).get("connect");
    if (!code) return;
    api.claimConnector(code).then(async (r) => {
      window.history.replaceState({}, "", window.location.pathname);
      if (r.changed) await refresh();
      dispatch({ type: "SET", patch: { connection: loadConnection(r.shop_code) } });
    }).catch((e) => toast.error(apiError(e)));
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const open = state.sidebarOpen ?? true;
  // the floating X-Visual button follows the hub tab: live for customers (owner switch, or this shop's own setting / early unlock)
  const visualOpen = user?.role === "admin" || (state.liveServices?.Visual !== false && (!user?.services || user.services.includes("Visual"))) || (user?.unlocked_services || []).includes("Visual");
  const closeSimulator = useCallback(() => dispatch({ type: "SET", patch: { simulator: false } }), [dispatch]);
  const closeShop = useCallback(() => dispatch({ type: "SET", patch: { shop: false } }), [dispatch]);
  const cutFromShop = useCallback((vehicle) => { dispatch({ type: "SET", patch: { shop: false } }); setTimeout(() => window.dispatchEvent(new CustomEvent("xpattern:find-vehicle", { detail: vehicle })), 50); }, [dispatch]);

  return (
    <div className="flex h-[100dvh] w-screen flex-col overflow-hidden bg-[#0c1017] text-[#e8e9ec]" data-testid="workspace">
      <OfflineBanner />
      <ViewingAsBanner />
      <OwnerNotice />
      <OfflineModeBanner />
      <ErrorBoundary name="menu"><MenuBar /></ErrorBoundary>
      <ErrorBoundary name="toolbar"><Toolbar /></ErrorBoundary>
      <TrialBanner />
      <div className="relative flex min-h-0 flex-1">
        {open && <ErrorBoundary name="sidebar"><Sidebar /></ErrorBoundary>}
        <main className="relative min-w-0 flex-1"><ErrorBoundary name="canvas">{state.stickerStudio ? <StickerStudio key={state.stickerStudio.nonce || state.stickerStudio.id || "new"} /> : state.preview ? <DecalPreview /> : <><FilmCanvas /><div className="pointer-events-none absolute inset-x-0 bottom-8 z-10 flex justify-center px-2"><CutBoardBar /></div></>}</ErrorBoundary>
          {!state.simulator && state.hubShown?.Visual !== false && visualOpen && <button type="button" data-testid="menu-simulator" onClick={() => dispatch({ type: "SET", patch: { simulator: true } })} title="X-Visual — show a customer every tint shade and wrap color on a real car" className="visual-fab">
            <span className="fab-cta">Click Here:</span><img src="/xpattern-x.png" alt="X" className="h-6 w-auto" draggable={false} /><em>-Visual</em>
          </button>}
          {!state.simulator && !state.stickerStudio && !state.preview && state.newVehicles > 0 && <div className="new-legend" data-testid="new-pattern-legend">
            <span className="picker-dot picker-dot-new" /><span>Yellow dot next to a vehicle model means it was added recently</span>
          </div>}
        </main>
      </div>
      <ErrorBoundary name="dialog">
        {state.simulator && <Suspense fallback={<div className="sim-root" data-testid="simulator-loading" />}><Simulator onClose={closeSimulator} /></Suspense>}
        {state.shop && <Suspense fallback={<div className="shop-root" data-testid="shop-loading" />}><ShopPanel onClose={closeShop} onCut={cutFromShop} /></Suspense>}
        <CutDialog />
        <NoPlotterDialog />
        <RollErrorDialog />
        <TrialAlertDialog />
        <PasswordPromptDialog />
        <HonorDialog />
        <SettingsDialog />
        <StripFitDialog />
        <CalibrationDialog />
        <ProfileDialog />
        <VersionHistoryDialog />
        <JobHistoryDialog />
        <SupportedPlottersDialog />
        <VevorGuideDialog />
        <WatchVideoDialog />
        <ImportDialog />
        <SubscribeDialog />
        <AdminDialog />
        <RequestVehicleDialog />
      </ErrorBoundary>
    </div>
  );
};

const Gate = () => {
  const { user, previewAs } = useAuth();
  const [welcomePlan, clearWelcome] = useWelcomePending();
  const path = window.location.pathname;
  if (["/home", "/pricing", "/contact", "/download", "/privacy", "/terms"].includes(path) && isStoreBuild()) { // installed Store app: the marketing site without installer links
    window.location.replace(path === "/home" || path === "/download" ? "/store" : `/store${path}`); return null;
  }
  if (["/home", "/pricing", "/contact", "/download", "/privacy", "/terms"].includes(path) || path.startsWith("/store")) return <Site />;
  if (path.replace(/\/$/, "") === "/demo") { window.location.replace("/download"); return null; } // demos now start from the download buttons
  if (path === "/payment/success") return <PaymentResultPage />;
  if (path === "/payment/cancel") return <PaymentResultPage cancelled />;
  if (path === "/pay/done") return <PayDone />;
  if (path.startsWith("/q/")) return <EstimatePage token={path.slice(3).replace(/\/$/, "")} />;
  if (path.startsWith("/i/")) return <InvoicePage token={path.slice(3).replace(/\/$/, "")} />;
  if (path.startsWith("/book/")) return <BookPage slug={path.slice(6).replace(/\/$/, "").toLowerCase()} embed={new URLSearchParams(window.location.search).get("embed") === "1"} />;
  if (path.startsWith("/offer/")) return <OfferLanding token={decodeURIComponent(path.slice(7).replace(/\/$/, ""))} />;
  if (path.startsWith("/files/")) return <BuyerLanding token={decodeURIComponent(path.slice(7).replace(/\/$/, ""))} />;
  if (user === null) return <div className="flex h-[100dvh] items-center justify-center bg-[#05070b] text-[#9aa0a8]" data-testid="auth-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  const demoCode = demoCodeFromUrl();
  if (demoCode && user !== null) return <DemoLanding code={demoCode} switching={Boolean(user && !user.demo)} />; // a demo link always opens the demo — even if the owner/shop is signed in
  const viewing = Boolean(user?.viewing_as); // owner looking into a customer account: no gates, no welcome tour
  const pending = user && !viewing && user.plan_state === "pending_payment";
  const renewal = Boolean(user && !viewing && !pending && user.renewal_required); // payment failed / subscription ended: locked out until a payment goes through
  const welcome = Boolean(user && !viewing && !pending && !renewal && welcomePlan);
  const terms = Boolean(user && !viewing && !pending && !renewal && !previewAs && needsTerms(user));
  const locked = !user || pending || renewal || welcome || terms;
  return (
    <WorkspaceProvider>
      <PreviewBar />
      <div className={locked ? "pointer-events-none select-none opacity-60 grayscale-[0.3]" : ""} aria-hidden={locked} data-testid={locked ? "workspace-disabled" : undefined}><Workspace locked={locked} /></div>
      {!user ? <AuthPage demo={Boolean(previewAs)} /> : pending ? <PaymentGate demo={Boolean(previewAs)} /> : renewal ? <RenewalGate demo={Boolean(previewAs)} /> : terms ? <TermsGate /> : welcome ? <WelcomeDialog plan={welcomePlan} name={user.name?.split(" ")[0]} days={user.trial_days_left || 3} onOk={() => { if (welcomePlan === "demo" && user.demo) api.demoAck().catch(() => {}); clearWelcome(); startTour(); }} /> : null}
      {user && !locked && <GuidedTour />}
      {user ? (!locked || renewal) && !viewing && <WelcomeIntro user={user} /> : <WelcomeIntro welcome />}
      <IntroPreview user={user} />
    </WorkspaceProvider>
  );
};

// Referral link (https://app.xpattern.net/?ref=CODE): count the click once and remember the code until the account is created.
(() => {
  try {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref && /^[A-Za-z0-9_-]{2,24}$/.test(ref)) {
      localStorage.setItem("xp.ref", ref.toUpperCase());
      api.promoClick(ref).catch(() => {});
      const q = new URLSearchParams(window.location.search); q.delete("ref");
      window.history.replaceState({}, "", `${window.location.pathname}${q.toString() ? `?${q}` : ""}`);
    }
  } catch { /* storage blocked */ }
})();

export default function App() {
  return (
    <ErrorBoundary name="app" full>
      <AuthProvider>
        <Gate />
      </AuthProvider>
      <Toaster theme="dark" position="bottom-right" richColors={false} toastOptions={{ unstyled: true, classNames: { toast: "toast3d", title: "toast3d-title", description: "toast3d-desc", icon: "toast3d-icon", success: "is-success", error: "is-error", info: "is-info", warning: "is-warning", actionButton: "toast3d-action", cancelButton: "toast3d-cancel", closeButton: "toast3d-close" } }} />
    </ErrorBoundary>
  );
}
